import React, { useEffect, useState } from 'react';

const DashboardBlogViewer = ({ onClose }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPostUrl, setSelectedPostUrl] = useState(null);
  const [selectedPostTitle, setSelectedPostTitle] = useState('');

  const WP_API_URL = 'https://mycashcanvas.com/wp/wp-json/wp/v2/posts';

  useEffect(() => {
    setLoading(true);
    fetch(`${WP_API_URL}?per_page=20&_embed`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch posts: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setPosts(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching posts:', err);
        setError(err.message || 'Failed to load posts');
        setLoading(false);
      });
  }, []);

  if (loading) return <p className="text-center py-8">Loading posts…</p>;
  if (error) return <p className="text-center py-8 text-red-500">Error: {error}</p>;

  // Show actual WordPress post in iframe when selected
  if (selectedPostUrl) {
    return (
      <div className="flex flex-col h-full gap-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setSelectedPostUrl(null)} 
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors font-medium"
          >
            ← Back to posts
          </button>
          <h2 className="text-lg font-semibold flex-1">{selectedPostTitle}</h2>
          <a 
            href={selectedPostUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-4 py-2 bg-btn-primary text-white rounded hover:bg-opacity-90 transition-colors font-medium"
          >
            Open on WordPress ↗
          </a>
        </div>
        <div className="flex-1 bg-white rounded-xl shadow overflow-hidden border border-gray-200">
          <iframe 
            src={selectedPostUrl}
            title={selectedPostTitle}
            className="w-full h-full border-0"
            style={{ minHeight: '700px' }}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold">Blog & Updates</h3>
        <button 
          onClick={onClose} 
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
        >
          Close
        </button>
      </div>

      {posts.length === 0 ? (
        <p className="text-center text-gray-500 py-12">No posts available</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {posts.map((post) => {
            const featured = post._embedded?.['wp:featuredmedia']?.[0];
            const image = featured?.source_url || '';
            const excerpt = post.excerpt?.rendered || '';
            const postUrl = post.link || '';

            return (
              <div 
                key={post.id} 
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col border border-gray-100"
              >
                {image && (
                  <img 
                    src={image} 
                    alt={post.title.rendered} 
                    className="h-40 w-full object-cover"
                  />
                )}
                <div className="p-4 flex flex-col flex-1">
                  <h4 
                    className="font-bold text-base mb-2 line-clamp-2" 
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                  />
                  <div 
                    className="text-sm text-gray-600 mb-4 flex-1 line-clamp-3" 
                    dangerouslySetInnerHTML={{ __html: excerpt }}
                  />
                  <div className="flex gap-2 mt-auto">
                    <button 
                      onClick={() => {
                        setSelectedPostUrl(postUrl);
                        setSelectedPostTitle(post.title.rendered.replace(/<[^>]*>/g, ''));
                      }}
                      className="flex-1 px-3 py-2 rounded bg-btn-secondary text-white hover:bg-opacity-90 transition-colors font-medium text-sm"
                    >
                      Read
                    </button>
                    <a 
                      href={postUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="px-3 py-2 rounded bg-btn-primary text-white hover:bg-opacity-90 transition-colors font-medium text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ↗
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardBlogViewer;
